'use client';

import * as React from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { IconChevronDown } from 'icon/Arrows/chevron-down';
import { IconLoading02 } from 'icon/General/loading-02';
import { IconUser01 } from 'icon/Users/user-01';
import { useAtom, useAtomValue } from 'jotai';
import { commentPollingInterval, retryCommentRead } from 'lib/comment-read';
import type { CommentList, PublicComment } from 'lib/comments';
import { useFormatter, useTranslations } from 'next-intl';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  cn,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'ui';
import { CommentAccount } from './comment-account';
import {
  commentOpenAtom,
  fetchCommentList,
  useCommentMutation,
} from './comment-api';
import { CommentAvatar } from './comment-avatar';
import { CommentContent } from './comment-content';
import { COMMENT_BUTTON, CommentEditor } from './comment-editor';
import { CommentErrorMessage, CommentIdentity } from './comment-identity';
import { CommentLikeButton } from './comment-like-button';
import { CommentNotifications } from './comment-notifications';

export function CommentPanel({
  postId,
  initialCount = 0,
  manage = false,
  preview = false,
}: {
  postId: string;
  initialCount?: number;
  manage?: boolean;
  preview?: boolean;
}) {
  const t = useTranslations('Blog.Comments');
  const client = useQueryClient();
  const [sort, setSort] = React.useState('newest');
  const [openPosts, setOpenPosts] = useAtom(commentOpenAtom);
  const expanded = !!openPosts[postId];
  const [opened, setOpened] = React.useState(false);
  const [signInOpen, setSignInOpen] = React.useState(false);
  const contentId = React.useId();
  const mutation = useCommentMutation(postId, manage);
  React.useEffect(() => {
    const onHashChange = () => {
      if (window.location.hash === '#comment')
        setOpenPosts((previous) => ({ ...previous, [postId]: true }));
    };
    onHashChange();
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [postId, setOpenPosts]);
  React.useEffect(() => {
    if (expanded) setOpened(true);
  }, [expanded]);
  const query = useInfiniteQuery({
    queryKey: ['comments', postId, manage, sort],
    queryFn: ({ pageParam, signal }) =>
      fetchCommentList(postId, sort, pageParam, manage, undefined, signal),
    initialPageParam: 0,
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === postId &&
      previousQuery.queryKey[2] === manage
        ? previous
        : undefined,
    getNextPageParam: (last, pages) =>
      last.hasMore ? pages.length : undefined,
    enabled: !preview,
    meta: { commentPolling: expanded },
    refetchInterval: (query) =>
      expanded
        ? commentPollingInterval(client, query.state.error, 15000)
        : false,
    refetchOnWindowFocus: true,
    retry: retryCommentRead,
  });
  const data = query.data?.pages[0];
  const count = data?.count ?? initialCount;
  const comments = Array.from(
    new Map(
      query.data?.pages
        .flatMap((page) => page.comments)
        .map((comment) => [comment.id, comment]),
    ).values(),
  );
  const signIn = () => setSignInOpen(true);
  return (
    <section className="bg-primary text-primary" aria-label={t('Title')}>
      <div className="flex items-center justify-between gap-3">
        <h2 className="min-w-0 flex-1">
          <button
            id={`comment-toggle-${postId}`}
            type="button"
            className="flex min-h-11 items-center gap-2 rounded text-base font-semibold text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
            aria-expanded={expanded}
            aria-controls={contentId}
            onClick={() =>
              setOpenPosts((previous) => ({ ...previous, [postId]: !expanded }))
            }
          >
            <span>{t('Title')}</span>
            {count > 0 && (
              <span className="text-xs tabular-nums text-tertiary">
                {count}
              </span>
            )}
            <IconChevronDown
              className={`size-3.5 text-tertiary transition-transform duration-150 motion-reduce:transition-none ${expanded ? 'rotate-180' : ''}`}
            />
          </button>
        </h2>
        {!preview && (
          <div className="flex shrink-0 items-center gap-1">
            <CommentNotifications
              postId={postId}
              manage={manage}
              viewer={data?.viewer ?? null}
              loading={query.isPending}
              onSignIn={signIn}
            />
            {data?.viewer ? (
              <CommentAccount
                postId={postId}
                viewer={data.viewer}
                manage={manage}
              />
            ) : (
              <button
                type="button"
                aria-label={t('SignIn')}
                title={t('SignIn')}
                disabled={query.isPending}
                onClick={signIn}
                className="inline-flex size-9 items-center justify-center rounded-full text-tertiary hover:bg-secondary-hover hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 disabled:opacity-40"
              >
                <IconUser01 aria-hidden="true" className="size-5" />
              </button>
            )}
          </div>
        )}
      </div>
      {!preview && data && (
        <CommentIdentity
          key={postId}
          postId={postId}
          open={signInOpen}
          onOpenChange={setSignInOpen}
          manage={manage}
        />
      )}
      <div id={contentId} hidden={!expanded}>
        {(opened || expanded) && (
          <div className="space-y-5 pt-4">
            {preview ? (
              <p className="text-sm text-tertiary">{t('Preview')}</p>
            ) : (
              <>
                {data && (
                  <>
                    {data.viewer ? (
                      <div className="space-y-3">
                        <CommentEditor
                          signedIn
                          pending={mutation.isPending}
                          label={t('Post')}
                          onSubmit={async (document) => {
                            await mutation.mutateAsync({
                              action: 'create',
                              document,
                            });
                          }}
                        />
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="flex min-h-12 w-full items-center gap-3 rounded-lg border border-secondary px-3 text-start text-sm text-tertiary hover:border-primary hover:text-secondary"
                        onClick={signIn}
                      >
                        <IconUser01 className="size-4 shrink-0" />
                        <span>{t('WriteComment')}</span>
                        <span className="ms-auto text-xs font-medium text-secondary">
                          {t('SignIn')}
                        </span>
                      </button>
                    )}
                    {mutation.error && (
                      <CommentErrorMessage code={mutation.error.message} />
                    )}
                  </>
                )}
                {query.isPending && <CommentSkeleton />}
                {query.error && (
                  <div className="space-y-2">
                    <CommentErrorMessage code={query.error.message} />
                    <button
                      className={COMMENT_BUTTON}
                      onClick={() => query.refetch()}
                    >
                      {t('Retry')}
                    </button>
                  </div>
                )}
                {comments.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex justify-end">
                      <Select value={sort} onValueChange={setSort}>
                        <SelectTrigger
                          aria-label={t('Sort')}
                          aria-busy={query.isPlaceholderData}
                          hideIcon={query.isPlaceholderData}
                          className="h-8 w-auto min-w-28 gap-2 border-secondary px-2.5 py-1 text-xs shadow-none hover:bg-secondary-hover"
                        >
                          <SelectValue />
                          {query.isPlaceholderData && (
                            <IconLoading02
                              aria-hidden="true"
                              className="size-3.5 shrink-0 animate-spin motion-reduce:animate-none"
                            />
                          )}
                        </SelectTrigger>
                        <SelectContent align="end">
                          <SelectItem
                            value="newest"
                            className="py-2 text-sm font-medium"
                          >
                            {t('Newest')}
                          </SelectItem>
                          <SelectItem
                            value="oldest"
                            className="py-2 text-sm font-medium"
                          >
                            {t('Oldest')}
                          </SelectItem>
                          <SelectItem
                            value="popular"
                            className="py-2 text-sm font-medium"
                          >
                            {t('Popular')}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div
                      aria-busy={query.isPlaceholderData}
                      className={`divide-y divide-secondary transition-opacity duration-150 motion-reduce:transition-none ${query.isPlaceholderData ? 'opacity-50' : ''}`}
                    >
                      {comments.map((comment) => (
                        <CommentThread
                          key={comment.id}
                          comment={comment}
                          postId={postId}
                          data={data!}
                          manage={manage}
                          onSignIn={signIn}
                        />
                      ))}
                    </div>
                  </div>
                )}
                {query.hasNextPage && (
                  <button
                    className={cn(COMMENT_BUTTON, 'w-full text-xs')}
                    disabled={
                      query.isFetchingNextPage || query.isPlaceholderData
                    }
                    onClick={() => query.fetchNextPage()}
                  >
                    {query.isFetchingNextPage ? t('Loading') : t('LoadMore')}
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function CommentThread({ comment, postId, data, manage, onSignIn }: ItemProps) {
  const [expanded, setExpanded] = React.useState(false);
  const t = useTranslations('Blog.Comments');
  return (
    <div className="py-5">
      <CommentItem
        comment={comment}
        postId={postId}
        data={data}
        manage={manage}
        onSignIn={onSignIn}
        onReplyCreated={() => setExpanded(true)}
      />
      {comment.replyCount > 0 && (
        <button
          type="button"
          className="mt-3 text-sm font-medium text-brand-secondary hover:underline"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded
            ? t('HideReplies')
            : t('Replies', { count: comment.replyCount })}
        </button>
      )}
      {expanded && (
        <CommentReplies
          rootId={comment.id}
          postId={postId}
          data={data}
          manage={manage}
          onSignIn={onSignIn}
        />
      )}
    </div>
  );
}

function CommentReplies({
  rootId,
  postId,
  data,
  manage,
  onSignIn,
}: Omit<ItemProps, 'comment'> & { rootId: string }) {
  const t = useTranslations('Blog.Comments');
  const client = useQueryClient();
  const expanded = !!useAtomValue(commentOpenAtom)[postId];
  const query = useInfiniteQuery({
    queryKey: ['comments', postId, manage, 'replies', rootId],
    queryFn: ({ pageParam, signal }) =>
      fetchCommentList(postId, 'oldest', pageParam, manage, rootId, signal),
    initialPageParam: 0,
    getNextPageParam: (last, pages) =>
      last.hasMore ? pages.length : undefined,
    enabled: expanded,
    meta: { commentPolling: expanded },
    refetchInterval: (query) =>
      expanded
        ? commentPollingInterval(client, query.state.error, 15000)
        : false,
    retry: retryCommentRead,
  });
  const comments = Array.from(
    new Map(
      query.data?.pages
        .flatMap((page) => page.comments)
        .map((comment) => [comment.id, comment]),
    ).values(),
  );
  return (
    <div className="ms-3 mt-4 space-y-5 border-s border-secondary ps-3 sm:ms-5 sm:ps-4">
      {query.isPending && <CommentSkeleton />}
      {query.error && <CommentErrorMessage code={query.error.message} />}
      {comments.map((comment) => (
        <CommentItem
          key={comment.id}
          comment={comment}
          postId={postId}
          data={query.data?.pages[0] ?? data}
          manage={manage}
          onSignIn={onSignIn}
        />
      ))}
      {query.hasNextPage && (
        <button
          type="button"
          className={cn(COMMENT_BUTTON, 'text-xs')}
          disabled={query.isFetchingNextPage}
          onClick={() => query.fetchNextPage()}
        >
          {query.isFetchingNextPage ? t('Loading') : t('LoadMore')}
        </button>
      )}
    </div>
  );
}

type ItemProps = {
  comment: PublicComment;
  postId: string;
  data: CommentList;
  manage: boolean;
  onSignIn: () => void;
  onReplyCreated?: () => void;
};

function CommentItem({
  comment,
  postId,
  data,
  manage,
  onSignIn,
  onReplyCreated,
}: ItemProps) {
  const t = useTranslations('Blog.Comments');
  const format = useFormatter();
  const [mode, setMode] = React.useState<'reply' | 'edit'>();
  const [deleting, setDeleting] = React.useState(false);
  const mutation = useCommentMutation(postId, manage);
  const liked =
    mutation.isPending && mutation.variables?.action === 'vote'
      ? mutation.variables.liked
      : comment.liked;
  const likes = Math.max(
    0,
    comment.likes + Number(liked) - Number(comment.liked),
  );
  return (
    <article id={`comment-${comment.id}`} className="min-w-0 space-y-2">
      <div className="flex items-start gap-3">
        <CommentAvatar
          name={comment.deleted ? '–' : comment.author.name}
          image={comment.author.avatar}
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
            <span className="break-words text-sm font-semibold text-primary">
              {comment.deleted ? t('DeletedAuthor') : comment.author.name}
            </span>
            {comment.author.isOwner && !comment.deleted && (
              <span className="rounded bg-secondary px-1.5 py-0.5 text-tertiary">
                {t('Owner')}
              </span>
            )}
            {(comment.pinnedAt || comment.hasPinnedReplies) && (
              <span className="font-medium text-brand-secondary">
                {t(comment.pinnedAt ? 'Pinned' : 'PinnedReplies')}
              </span>
            )}
            <time
              className="text-tertiary"
              dateTime={comment.createdAt}
              title={format.dateTime(new Date(comment.createdAt), {
                dateStyle: 'medium',
                timeStyle: 'short',
              })}
            >
              {format.dateTime(new Date(comment.createdAt), {
                month: 'short',
                day: 'numeric',
              })}
            </time>
            {comment.updatedAt && (
              <span className="text-tertiary">{t('Edited')}</span>
            )}
          </div>
          {comment.deleted ? (
            <p className="my-2 text-sm italic text-tertiary">{t('Deleted')}</p>
          ) : (
            comment.document && <CommentContent document={comment.document} />
          )}
          {!comment.deleted && (
            <div className="mt-2 flex flex-wrap gap-3 text-xs font-medium text-tertiary">
              <CommentLikeButton
                liked={liked}
                likes={likes}
                pending={mutation.isPending}
                onClick={() =>
                  data.viewer
                    ? mutation.mutate({
                        action: 'vote',
                        commentId: comment.id,
                        liked: !liked,
                      })
                    : onSignIn()
                }
              />
              <button
                type="button"
                className="rounded py-1 hover:text-primary"
                onClick={() =>
                  data.viewer
                    ? setMode(mode === 'reply' ? undefined : 'reply')
                    : onSignIn()
                }
              >
                {t('Reply')}
              </button>
              {comment.canEdit && (
                <button
                  type="button"
                  className="rounded py-1 hover:text-primary"
                  onClick={() => setMode(mode === 'edit' ? undefined : 'edit')}
                >
                  {t('Edit')}
                </button>
              )}
              {data.canModerate && (
                <button
                  type="button"
                  disabled={mutation.isPending}
                  className="rounded py-1 hover:text-primary"
                  onClick={() =>
                    data.viewer
                      ? mutation.mutate({
                          action: 'pin',
                          commentId: comment.id,
                          pinned: !comment.pinnedAt,
                        })
                      : onSignIn()
                  }
                >
                  {comment.pinnedAt ? t('Unpin') : t('Pin')}
                </button>
              )}
              {comment.canDelete && (
                <button
                  type="button"
                  className="rounded py-1 hover:text-error-primary"
                  onClick={() => (data.viewer ? setDeleting(true) : onSignIn())}
                >
                  {t('Delete')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      {mode && (
        <div className="pt-3">
          <p className="mb-2 text-xs text-tertiary">
            {mode === 'reply'
              ? t('ReplyingTo', { name: comment.author.name })
              : t('Editing')}
          </p>
          <CommentEditor
            key={mode}
            initialDocument={
              mode === 'edit' ? (comment.document ?? undefined) : undefined
            }
            pending={mutation.isPending}
            signedIn={!!data.viewer}
            label={mode === 'reply' ? t('Reply') : t('Save')}
            onCancel={() => setMode(undefined)}
            onSubmit={async (document) => {
              await mutation.mutateAsync(
                mode === 'reply'
                  ? { action: 'create', parentId: comment.id, document }
                  : { action: 'edit', commentId: comment.id, document },
              );
              if (mode === 'reply') onReplyCreated?.();
              setMode(undefined);
            }}
          />
        </div>
      )}
      {mutation.error && <CommentErrorMessage code={mutation.error.message} />}
      <AlertDialog open={deleting} onOpenChange={setDeleting}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('DeleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('DeleteDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('Cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={mutation.isPending}
              onClick={() =>
                mutation.mutate(
                  { action: 'delete', commentId: comment.id },
                  {
                    onSuccess: () => {
                      setDeleting(false);
                      setMode(undefined);
                    },
                  },
                )
              }
            >
              {t('Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </article>
  );
}

function CommentSkeleton() {
  return (
    <div aria-busy="true" className="min-h-64 space-y-4" role="status">
      <div className="h-28 animate-pulse rounded-lg bg-secondary" />
      {[0, 1].map((item) => (
        <div key={item} className="space-y-2">
          <div className="h-4 w-1/3 animate-pulse rounded bg-secondary" />
          <div className="h-10 animate-pulse rounded bg-secondary" />
        </div>
      ))}
    </div>
  );
}
